// Buchhaltung & Kasse.
//
// Zwei Blickwinkel auf dieselben Daten:
//   * Der FAHRER will am Stop wissen: Hat der Kunde beim letzten Mal bezahlt?
//   * Die LEITUNG will wissen: Was ist offen, was liegt in der Kasse?

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type RechnungStatus = "draft" | "open" | "partly_paid" | "paid" | "overdue" | "cancelled";

export interface Rechnung {
  id: string;
  invoice_number: string | null;
  order_id: string | null;
  customer_id: string;
  status: RechnungStatus;
  total_cents: number;
  paid_cents: number;
  due_at: string | null;
  paid_at: string | null;
  period_start: string;
  created_at: string;
  pdf_url?: string | null;
  shipping_cents?: number;
  customer?: { display_name: string; customer_number: string | null } | null;
  slip?: { slip_number: string | null; signature_url: string | null }[] | null;
}

/** Ist diese Rechnung überfällig? Der Status allein sagt es nicht — er wird nicht nachgeführt. */
export function istUeberfaellig(r: Rechnung) {
  if (r.status === "paid" || r.status === "cancelled") return false;
  if (!r.due_at) return false;
  return new Date(r.due_at) < new Date(new Date().toDateString());
}

/**
 * Alle Belege eines Kunden — für die Kundenakte und die Stop-Karte.
 *
 * Bewusst OHNE Join auf delivery_slips: zwischen invoices und delivery_slips
 * gibt es keine direkte Beziehung (beide hängen an orders). PostgREST kann so
 * einen Join nicht auflösen und lässt die ganze Abfrage fehlschlagen — die
 * Belege wären dann still leer.
 */
export function useKundenRechnungen(customerId: string | undefined) {
  return useQuery({
    queryKey: ["rechnungen-kunde", customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("customer_id", customerId!)
        .order("period_start", { ascending: false })
        .limit(24);
      if (error) throw error;
      return (data ?? []) as unknown as Rechnung[];
    },
  });
}

export interface Zahlungsstand {
  offeneAnzahl: number;
  offenCents: number;
  letzte?: { bezahlt: boolean; datum: string; betrag: number };
}

/**
 * Kurzfassung für die Stop-Karte des Fahrers: Steht noch was offen, und hat der
 * Kunde beim letzten Mal bezahlt? Genau das, was man an der Haustür wissen muss.
 */
export function zahlungsstand(rechnungen: Rechnung[]): Zahlungsstand {
  const relevant = rechnungen.filter((r) => r.status !== "cancelled");
  const offen = relevant.filter((r) => r.status !== "paid");
  const letzte = relevant[0];

  return {
    offeneAnzahl: offen.length,
    offenCents: offen.reduce((s, r) => s + (r.total_cents - r.paid_cents), 0),
    letzte: letzte
      ? {
          bezahlt: letzte.status === "paid",
          datum: letzte.period_start,
          betrag: letzte.total_cents,
        }
      : undefined,
  };
}

/** Zahlungsstand aller Kunden einer Tour — EIN Query statt 221. */
export function useZahlungsstandFuerKunden(customerIds: string[]) {
  const key = [...customerIds].sort().join(",");
  return useQuery({
    queryKey: ["zahlungsstand", key],
    enabled: customerIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("customer_id, status, total_cents, paid_cents, due_at, period_start, id, invoice_number, order_id, paid_at, created_at")
        .in("customer_id", customerIds)
        .order("period_start", { ascending: false });
      if (error) throw error;

      const map = new Map<string, Rechnung[]>();
      for (const r of (data ?? []) as unknown as Rechnung[]) {
        const liste = map.get(r.customer_id) ?? [];
        liste.push(r);
        map.set(r.customer_id, liste);
      }

      const out = new Map<string, Zahlungsstand>();
      for (const [cid, liste] of map) out.set(cid, zahlungsstand(liste));
      return out;
    },
  });
}

// ═══════════════════════════════════════════════════ Leitung

export interface KassenLage {
  offeneRechnungen: number;
  offenCents: number;
  ueberfaellig: number;
  ueberfaelligCents: number;
  barImUmlauf: number; // vom Fahrer kassiert, noch nicht übergeben
  umsatzMonatCents: number;
}

export function useKassenLage() {
  return useQuery({
    queryKey: ["kassenlage"],
    queryFn: async (): Promise<KassenLage> => {
      const monatsStart = new Date();
      monatsStart.setDate(1);
      monatsStart.setHours(0, 0, 0, 0);

      const [{ data: rechnungen }, { data: bar }] = await Promise.all([
        supabase.from("invoices").select("status, total_cents, paid_cents, due_at, period_start"),
        supabase
          .from("payments")
          .select("amount_cents, status")
          .eq("method", "cash")
          .eq("status", "cash_pending_handover"),
      ]);

      const alle = (rechnungen ?? []) as unknown as Rechnung[];
      const offen = alle.filter((r) => r.status !== "paid" && r.status !== "cancelled");
      const spaet = offen.filter((r) => istUeberfaellig(r));

      return {
        offeneRechnungen: offen.length,
        offenCents: offen.reduce((s, r) => s + (r.total_cents - r.paid_cents), 0),
        ueberfaellig: spaet.length,
        ueberfaelligCents: spaet.reduce((s, r) => s + (r.total_cents - r.paid_cents), 0),
        barImUmlauf: (bar ?? []).reduce((s, p) => s + (p.amount_cents ?? 0), 0),
        umsatzMonatCents: alle
          .filter((r) => r.status !== "cancelled" && new Date(r.period_start) >= monatsStart)
          .reduce((s, r) => s + r.total_cents, 0),
      };
    },
    staleTime: 30_000,
  });
}

export type RechnungsFilter = "offen" | "ueberfaellig" | "bezahlt" | "alle";

export function useRechnungen(filter: RechnungsFilter) {
  return useQuery({
    queryKey: ["rechnungen", filter],
    queryFn: async () => {
      let q = supabase
        .from("invoices")
        .select("*, customer:customers(display_name, customer_number)")
        .order("period_start", { ascending: false })
        .limit(300);

      if (filter === "bezahlt") q = q.eq("status", "paid");
      if (filter === "offen" || filter === "ueberfaellig") {
        q = q.not("status", "in", '("paid","cancelled")');
      }

      const { data, error } = await q;
      if (error) throw error;

      const liste = (data ?? []) as unknown as Rechnung[];
      // Überfällig lässt sich nicht sinnvoll in SQL filtern, ohne den Status
      // nachzuführen — also hier, wo das Datum ohnehin bekannt ist.
      return filter === "ueberfaellig" ? liste.filter(istUeberfaellig) : liste;
    },
  });
}

/** Rechnung von Hand als bezahlt buchen (Überweisung eingegangen, Bar nachgereicht). */
export function useAlsBezahltBuchen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: { id: string; total_cents: number }) => {
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "paid",
          paid_cents: r.total_cents,
          paid_at: new Date().toISOString(),
        })
        .eq("id", r.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["rechnungen-kunde"] });
      qc.invalidateQueries({ queryKey: ["zahlungsstand"] });
    },
  });
}

/** Zurücknehmen — falls versehentlich gebucht. */
export function useZahlungZuruecknehmen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "open", paid_cents: 0, paid_at: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["rechnungen-kunde"] });
      qc.invalidateQueries({ queryKey: ["zahlungsstand"] });
    },
  });
}
